import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
}                                                    from '@angular/core';
import {CompositeFilterDescriptor, FilterDescriptor} from '@progress/kendo-data-query';
import {map, max, sortBy, uniq}                      from 'lodash';
import {FilterType}                                  from '../../../enums/filter-type';
import {ApKendoIconService}                          from '../../../../icons/ap-kendo-icon.service';

/**
 * the DataSource Filter Component inspired by Kendo
 */
@Component({
  selector: 'ap-crop-rotation-filter',
  templateUrl: './ap-crop-rotation-filter.component.html',
  styleUrls: ['./ap-crop-rotation-filter.component.scss']
})
export class ApCropRotationFilterComponent implements OnChanges {
  protected readonly ApKendoIconService = ApKendoIconService;
  public get ProcessFilterDisabled(): boolean {
    switch (this.filterType) {
      case FilterType.MULTI_SELECT:
        return !this.selected || this.selected.length < 1;
      case FilterType.RANGE:
        return false;
    }
  }

  private get MeaningKey(): string {
    return this.mapKey || this.key;
  }

  @ViewChild('Filter', {static: false}) private filterLink: ElementRef;
  @ViewChild('pop', {static: false, read: ElementRef}) private popup: ElementRef;
  /**
   * the Kendo Filter Descriptor
   */
  @Input() public filter: CompositeFilterDescriptor;
  /**
   * the DataSource of Values to select
   */
  @Input() public source: any[] = [];
  /**
   * the Key of the Property from DataSource (Grid)
   */
  @Input() public key = '';
  /**
   * other Property Key when the Grid DataSource Property is different to this selection Source (optional)
   */
  @Input() public mapKey: string;
  /**
   * some Metadata that is used in the buildFilter Method (optional)
   */
  @Input() public meta: any;
  /**
   * logic to build a Filter definition (optional)
   */
  @Input() public buildFilter: (s: any[], year: number, propertyName: string) => Array<CompositeFilterDescriptor | FilterDescriptor> = null;
  /**
   * the type of the Control
   */
  @Input() public filterType: FilterType = FilterType.MULTI_SELECT;

  /**
   * fires when the Filter was changed
   */
  @Output() public filterChange = new EventEmitter<CompositeFilterDescriptor>();

  /**
   * is the Filter visible
   */
  public show = false;
  public minValue: number;
  public maxValue: number;
  public recentNumberFilter: CompositeFilterDescriptor = null;
  public filterTypes = FilterType;
  /**
   * a List of selected Values for the Filter
   */
  public selected: any[] = [];

  /**
   * get the Selection Source as unique List
   */
  public distinctSource: any[] = [];
  public filteredSource: any[] = [];

  private _myFilter: CompositeFilterDescriptor = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.source) {
      setTimeout(this._createDistinctSource.bind(this), 1);
    }
  }

  @HostListener('keydown', ['$event'])
  public keydown(event: any): void {
    if (event.keyCode === 27) {
      this.show = false;
    }
  }

  @HostListener('document:click', ['$event'])
  public documentClick(event: any): void {
    if (!this._contains(event.target)) {
      this.show = false;
    }
  }

  /**
   * updates the current Filter with new Values
   */
  public processFilter(): void {
    if (this.filterType === FilterType.RANGE) {
      this._processRange();
    } else {
      this._processMultiSelect();
    }
  }

  /**
   * delete the current Filter
   */
  public clearFilter(): void {
    this.selected = [];
    this._resetRangeValues();
    this.show = false;
    this.processFilter();
  }

  public handleFilter(e: string): void {
    if (!e) {
      this.filteredSource = [...this.distinctSource];
    }
    if (this.distinctSource[0]) {
      if (typeof this.distinctSource[0].includes === typeof function (): void {
      } || typeof this.distinctSource[0] === typeof 0) {
        this.filteredSource = this.distinctSource.filter(i => i?.toString()?.toLowerCase()?.includes(e?.toLowerCase()));
      }
    }
  }

  public numberFilterChanged(filterDescriptor: FilterDescriptor[]): void {
    if (!filterDescriptor || filterDescriptor.length) {
      this.minValue = 0;
      this.maxValue = max(this.distinctSource);
    }

    const minFilter = filterDescriptor.find(_ => _.operator === 'gte');
    const maxFilter = filterDescriptor.find(_ => _.operator === 'lte');

    if (minFilter) {
      this.minValue = minFilter.value;
    } else {
      this.minValue = 0;
    }

    if (maxFilter) {
      this.maxValue = maxFilter.value;
    } else {
      this.maxValue = max(this.distinctSource);
    }

    this.recentNumberFilter = {logic: 'and', filters: filterDescriptor};
  }

  public isFilterActive(): boolean {
    if (this.filterType === FilterType.RANGE) {
      return this.recentNumberFilter && this.recentNumberFilter.filters?.length > 0;
    } else {
      return this.selected && this.selected?.length > 0;
    }
  }

  private _contains(target: any): boolean {
    return this.filterLink.nativeElement.contains(target) ||
      (this.popup ? this.popup.nativeElement.contains(target) : false);
  }

  private _createDistinctSource(): void {
    const uniqKeyValues = uniq(map(this.source, (i: any) => i ? i[this.key] : undefined));
    this.distinctSource = sortBy(uniqKeyValues, (i: any) => i);
    this.filteredSource = [...this.distinctSource];
    if (this.filterType === FilterType.RANGE) {
      this.minValue = 0;
      this.maxValue = max(this.distinctSource);
    }
  }

  private _processRange(): void {
    if (!this.filter) {
      this.filter = {
        logic: 'and',
        filters: [],
      };
    }
    this.filter.filters = this.filter.filters.FindAll(_ => _ !== this._myFilter);
    if (this.minValue === 0 && this.maxValue === max(this.filteredSource)) {
      this.filterChange.emit(this.filter);
      this.show = false;
      return;
    }
    this._myFilter = {
      logic: 'and',
      filters: [
        {
          field: this.MeaningKey,
          value: this.minValue,
          operator: 'gte'
        },
        {
          field: this.MeaningKey,
          value: this.maxValue,
          operator: 'lte'
        }
      ],
    };
    this.filter.filters.Add(this._myFilter);
    this.filterChange.emit(this.filter);
    this.show = false;
  }

  private _resetRangeValues(): void {
    if (this.filterType === FilterType.RANGE) {
      this.recentNumberFilter = null;
      this.minValue = 0;
      this.maxValue = max(this.distinctSource);
    }
  }

  private _processMultiSelect(): void {
    if (!this.filter) {
      this.filter = {
        logic: 'and',
        filters: [],
      };
    }
    this.filter.filters = this.filter.filters.FindAll(_ => _ !== this._myFilter);
    if (this.selected.length < 1) {
      this.filterChange.emit(this.filter);
      this.show = false;
      return;
    }
    let filters: CompositeFilterDescriptor = {
      logic: 'or',
      filters: this.selected.map(i => {
        return {
          field: this.MeaningKey,
          value: i,
          operator: 'eq'
        };
      }),
    };
    if (typeof this.buildFilter === 'function') {
      filters = {
        logic: 'or',
        filters: this.buildFilter(this.selected, this.meta.year, this.key),
      };
    }
    this._myFilter = filters.filters.length < 1 ? {
      logic: 'or',
      filters: [],
    } : filters;
    this.filter.filters.Add(this._myFilter);
    this.filterChange.emit(this.filter);
    this.show = false;
  }
}
